import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Package, Check, Palette, Upload, Trash2, AlertTriangle, CheckCircle2, AlertCircle, ShoppingBag, ZoomIn, ZoomOut, Eye, RotateCw, FlipHorizontal2, Grid3x3, AlignCenterHorizontal, AlignCenterVertical, QrCode, Type, Plus } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import CropModal from '@/components/CropModal';
import DraggableOverlay from '@/components/DraggableOverlay';
import PreviewModal from '@/components/PreviewModal';
import QRCodeTool from '@/components/QRCodeTool';
import TextLayer, { TextControlPanel } from '@/components/TextLayer';
import { useCart } from '@/context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;

function getResolution(width) {
  if (width >= 2000) return { level: 'professional', color: '#22C55E', bg: 'bg-green-500/10 border-green-500/20', label: 'Professional Quality', icon: CheckCircle2 };
  if (width >= 1000) return { level: 'studio', color: '#EAB308', bg: 'bg-yellow-500/10 border-yellow-500/20', label: 'Studio Touch-up Required', icon: AlertTriangle };
  return { level: 'low', color: '#EF4444', bg: 'bg-red-500/10 border-red-500/20', label: 'Low Quality Warning', icon: AlertCircle };
}

function isLightColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16); const g = parseInt(c.substr(2, 2), 16); const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

export default function ProductPage() {
  const { slug } = useParams();
  const location = useLocation();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeViewIdx, setActiveViewIdx] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [selectedFinish, setSelectedFinish] = useState(null);

  // Per-view images: { [viewId]: [ { id, userLogo, originalLogoData, originalWidth, logoPlacement } ] }
  const [viewLogos, setViewLogos] = useState({});
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [pendingOriginalWidth, setPendingOriginalWidth] = useState(0);
  const [pendingOriginalData, setPendingOriginalData] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [qty, setQty] = useState(1);

  // Zoom
  const [zoomLevel, setZoomLevel] = useState(0);
  const [panOrigin, setPanOrigin] = useState(null);
  const [panning, setPanning] = useState(false);

  // Feature toggles
  const [showPreview, setShowPreview] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showQRTool, setShowQRTool] = useState(false);

  // Per-view text layers
  const [viewTexts, setViewTexts] = useState({});
  const [activeTextId, setActiveTextId] = useState(null);

  // Selection: 'img-{id}' or 'text-{id}'
  const [selectedElement, setSelectedElement] = useState(null);

  const fileInputRef = useRef(null);
  const textZoneRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/shop/products/${slug}`)
      .then(res => { if (!res.ok) throw new Error('Not found'); return res.json(); })
      .then(data => {
        setProduct(data);
        const defaults = {};
        data.sub_options?.forEach(group => {
          if (group.options?.length > 0) defaults[group.name] = group.options[0];
        });
        setSelectedOptions(defaults);
        setSelectedColorIdx(0);
        setActiveViewIdx(0);
        if (data.finishes?.length > 0) setSelectedFinish(data.finishes[0]);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (location.state?.authComplete) { /* Cart drawer will auto-open from CartContext */ }
  }, [location.state]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setPendingOriginalWidth(img.naturalWidth);
        setPendingOriginalData(reader.result);
        setRawImageSrc(reader.result);
        setShowCropModal(true);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const views = product?.views || [];
  const activeView = views[activeViewIdx] || null;
  const activeViewId = activeView?.id;

  // Check if a view allows the selected finish
  const viewAllowsFinish = (view, finish) => {
    if (!view || !finish) return true;
    if (!view.allowed_finishes || view.allowed_finishes.length === 0) return true;
    return view.allowed_finishes.includes(finish.label);
  };

  const getEffectiveZone = (view) => {
    if (!view) return null;
    // If finish isn't allowed on this view, fall back to default print zone
    if (selectedFinish && viewAllowsFinish(view, selectedFinish) && view.finish_zones?.[selectedFinish.label]) {
      return view.finish_zones[selectedFinish.label];
    }
    return view.print_zone;
  };

  const activeViewFinishBlocked = activeView && selectedFinish && !viewAllowsFinish(activeView, selectedFinish);

  // Get the allowed finish names for a view (what it falls back to)
  const getViewFallbackFinish = (view) => {
    if (!view?.allowed_finishes || view.allowed_finishes.length === 0) return null;
    return view.allowed_finishes.join(' / ');
  };

  const fitLogoToZone = useCallback((cropWidth, cropHeight) => {
    const logoAspect = cropWidth / cropHeight;
    let w, h;
    if (logoAspect >= 1) { w = 100; h = 100 / logoAspect; }
    else { h = 100; w = 100 * logoAspect; }
    const x = (100 - w) / 2;
    const y = (100 - h) / 2;
    return { x, y, width: w, height: h, rotation: 0, flipH: false };
  }, []);

  // Current view images array
  const currentImages = (activeViewId && viewLogos[activeViewId]) || [];
  const hasAnyImageOnView = currentImages.length > 0;
  const selectedImg = currentImages.find(img => img.id === selectedImageId) || null;

  const handleCropConfirm = useCallback((croppedDataUrl, dimensions) => {
    if (!activeViewId) return;
    const placement = fitLogoToZone(dimensions?.width || 100, dimensions?.height || 100);
    const newImg = {
      id: `img-${Date.now()}`,
      userLogo: croppedDataUrl,
      originalLogoData: pendingOriginalData,
      originalWidth: pendingOriginalWidth,
      logoPlacement: placement,
    };
    setViewLogos(prev => ({
      ...prev,
      [activeViewId]: [...(prev[activeViewId] || []), newImg],
    }));
    setSelectedImageId(newImg.id);
    setSelectedElement(`img-${newImg.id}`);
    setShowCropModal(false);
    setRawImageSrc(null);
  }, [activeViewId, pendingOriginalData, pendingOriginalWidth, fitLogoToZone]);

  const handleRemoveImage = useCallback((imgId) => {
    if (!activeViewId) return;
    setViewLogos(prev => ({
      ...prev,
      [activeViewId]: (prev[activeViewId] || []).filter(img => img.id !== imgId),
    }));
    if (selectedImageId === imgId) setSelectedImageId(null);
  }, [activeViewId, selectedImageId]);

  const handlePlacementChange = useCallback((imgId, placement) => {
    if (!activeViewId) return;
    setViewLogos(prev => ({
      ...prev,
      [activeViewId]: (prev[activeViewId] || []).map(img =>
        img.id === imgId ? { ...img, logoPlacement: placement } : img
      ),
    }));
  }, [activeViewId]);

  // Tools work on selected element (image or text)
  const handleRotate = useCallback(() => {
    if (!activeViewId || !selectedElement) return;
    if (selectedElement.startsWith('img-')) {
      const imgId = selectedElement.replace('img-', '');
      setViewLogos(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(img =>
          img.id === imgId ? { ...img, logoPlacement: { ...img.logoPlacement, rotation: ((img.logoPlacement?.rotation || 0) + 90) % 360 } } : img
        ),
      }));
    } else if (selectedElement.startsWith('text-')) {
      const textId = selectedElement.replace('text-', '');
      setViewTexts(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(t =>
          t.id === textId ? { ...t, rotation: ((t.rotation || 0) + 90) % 360 } : t
        ),
      }));
    }
  }, [activeViewId, selectedElement]);

  const handleFlip = useCallback(() => {
    if (!activeViewId || !selectedElement) return;
    if (selectedElement.startsWith('img-')) {
      const imgId = selectedElement.replace('img-', '');
      setViewLogos(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(img =>
          img.id === imgId ? { ...img, logoPlacement: { ...img.logoPlacement, flipH: !img.logoPlacement?.flipH } } : img
        ),
      }));
    }
  }, [activeViewId, selectedElement]);

  const handleAlignH = useCallback(() => {
    if (!activeViewId || !selectedElement) return;
    if (selectedElement.startsWith('img-')) {
      const imgId = selectedElement.replace('img-', '');
      setViewLogos(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(img =>
          img.id === imgId ? { ...img, logoPlacement: { ...img.logoPlacement, x: (100 - img.logoPlacement.width) / 2 } } : img
        ),
      }));
    } else if (selectedElement.startsWith('text-')) {
      const textId = selectedElement.replace('text-', '');
      setViewTexts(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(t =>
          t.id === textId ? { ...t, x: 50 } : t
        ),
      }));
    }
  }, [activeViewId, selectedElement]);

  const handleAlignV = useCallback(() => {
    if (!activeViewId || !selectedElement) return;
    if (selectedElement.startsWith('img-')) {
      const imgId = selectedElement.replace('img-', '');
      setViewLogos(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(img =>
          img.id === imgId ? { ...img, logoPlacement: { ...img.logoPlacement, y: (100 - img.logoPlacement.height) / 2 } } : img
        ),
      }));
    } else if (selectedElement.startsWith('text-')) {
      const textId = selectedElement.replace('text-', '');
      setViewTexts(prev => ({
        ...prev,
        [activeViewId]: (prev[activeViewId] || []).map(t =>
          t.id === textId ? { ...t, y: 50 } : t
        ),
      }));
    }
  }, [activeViewId, selectedElement]);

  // QR Code placement (adds as new image)
  const handleQRGenerate = useCallback((dataUrl) => {
    if (!activeViewId) return;
    const placement = fitLogoToZone(200, 200);
    const newImg = {
      id: `img-${Date.now()}`,
      userLogo: dataUrl,
      originalLogoData: dataUrl,
      originalWidth: 200,
      logoPlacement: placement,
    };
    setViewLogos(prev => ({
      ...prev,
      [activeViewId]: [...(prev[activeViewId] || []), newImg],
    }));
    setSelectedImageId(newImg.id);
    setSelectedElement(`img-${newImg.id}`);
    setShowQRTool(false);
  }, [activeViewId, fitLogoToZone]);

  // Text layer management
  const currentTexts = (activeViewId && viewTexts[activeViewId]) || [];

  const handleAddText = useCallback(() => {
    if (!activeViewId) return;
    const newText = {
      id: Date.now().toString(),
      text: 'Your Text',
      fontFamily: 'Arial',
      fontSize: 24,
      color: '#ffffff',
      x: 20, y: 20,
      letterSpacing: 0,
      lineHeight: 1.2,
    };
    setViewTexts(prev => ({ ...prev, [activeViewId]: [...(prev[activeViewId] || []), newText] }));
    setActiveTextId(newText.id);
    setSelectedElement(`text-${newText.id}`);
  }, [activeViewId]);

  const handleUpdateText = useCallback((updatedText) => {
    if (!activeViewId) return;
    setViewTexts(prev => ({
      ...prev,
      [activeViewId]: (prev[activeViewId] || []).map(t => t.id === updatedText.id ? updatedText : t),
    }));
  }, [activeViewId]);

  const handleDeleteText = useCallback((textId) => {
    if (!activeViewId) return;
    setViewTexts(prev => ({
      ...prev,
      [activeViewId]: (prev[activeViewId] || []).filter(t => t.id !== textId),
    }));
    if (activeTextId === textId) setActiveTextId(null);
    if (selectedElement === `text-${textId}`) setSelectedElement(null);
  }, [activeViewId, activeTextId, selectedElement]);

  if (loading) return <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" /></div>;

  if (!product) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A]"><Navigation />
        <div className="pt-32 text-center">
          <Package className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#0A0A0A] dark:text-white">Product not found</h1>
          <Link to="/shop" className="mt-4 inline-flex items-center gap-2 text-sm text-[#00E5FF] hover:underline"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>
        </div>
      </div>
    );
  }

  const colors = product.color_variants || [];
  const selectedColor = colors[selectedColorIdx] || null;
  const productFinishes = product.finishes || [];
  const hasFinishes = productFinishes.length > 0;

  const firstImage = currentImages[0] || null;
  const resolution = firstImage?.originalWidth > 0 ? getResolution(firstImage.originalWidth) : null;
  const ResIcon = resolution?.icon;

  const optionsTotal = Object.values(selectedOptions).reduce((sum, opt) => sum + (opt?.price_modifier || 0), 0);
  const finishCost = selectedFinish?.price_modifier || 0;
  const viewPrintTotal = views.reduce((sum, v) => {
    if (!(viewLogos[v.id]?.length > 0)) return sum;
    return sum + (v.print_price || 0);
  }, 0);
  const totalPrice = product.base_price + optionsTotal + finishCost + viewPrintTotal;

  const viewsWithLogos = views.filter(v => viewLogos[v.id]?.length > 0).length;
  const hasAnyLogo = viewsWithLogos > 0;

  const getCurrentImage = () => {
    if (!activeView) return null;
    if (selectedColor?.images?.[activeView.id]) return selectedColor.images[activeView.id];
    if (activeView.image) return activeView.image;
    return null;
  };
  const currentImage = getCurrentImage();
  const currentZone = getEffectiveZone(activeView);

  const selectOption = (groupName, option) => setSelectedOptions(prev => ({ ...prev, [groupName]: option }));

  const isSoldOut = product.status === 'sold_out';
  const zoomScales = [1, 2.5, 5];
  const zoomed = zoomLevel > 0;
  const zoomScale = zoomScales[zoomLevel];
  const cycleZoom = () => { setZoomLevel(z => (z + 1) % 3); setPanOrigin(null); };

  const hasSelection = selectedElement !== null;
  const isImageSelected = selectedElement?.startsWith('img-');

  // Build cart-compatible viewLogos (first image per view for backwards compat)
  const buildCartViewLogos = () => {
    const result = {};
    for (const [viewId, images] of Object.entries(viewLogos)) {
      if (images.length > 0) {
        // Store all images — CartDrawer will handle array
        result[viewId] = images;
      }
    }
    return result;
  };

  const buildCartItem = () => ({
    product_slug: product.slug,
    product_title: product.title,
    product_image: getCurrentImage(),
    selectedColor: colors[selectedColorIdx] || null,
    selectedOptions,
    selectedFinish,
    activeViews: views,
    viewLogos: buildCartViewLogos(),
    viewTexts,
    totalPrice,
    base_price: product.base_price,
    qty,
  });

  const handleAddToCart = () => {
    if (!hasAnyLogo) { fileInputRef.current?.click(); return; }
    addItem(buildCartItem(), false);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleCheckoutNow = () => {
    if (!hasAnyLogo) { fileInputRef.current?.click(); return; }
    addItem(buildCartItem(), true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead title={`${product.title} | Custom Print Shop`} description={product.description?.replace(/<[^>]*>/g, '').slice(0, 155)} path={`/shop/${product.slug}`} />
      <Navigation />

      <section className="pt-24 pb-16 md:pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-[#00E5FF] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to shop
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Product Canvas */}
            <div>
              <div className="aspect-square bg-white rounded-2xl border border-slate-200 dark:border-white/5 relative overflow-hidden" data-testid="product-canvas"
                onMouseDown={zoomed ? (e) => {
                  if (e.target.closest('[data-testid="toolbar-group"]')) return;
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const startX = e.clientX; const startY = e.clientY;
                  const origin = panOrigin || { x: currentZone?.x + currentZone?.width / 2 || 50, y: currentZone?.y + currentZone?.height / 2 || 50 };
                  setPanning(true);
                  const move = (ev) => { const dx = (ev.clientX - startX) / rect.width * -40; const dy = (ev.clientY - startY) / rect.height * -40; setPanOrigin({ x: Math.max(0, Math.min(100, origin.x + dx)), y: Math.max(0, Math.min(100, origin.y + dy)) }); };
                  const up = () => { setPanning(false); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
                  window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
                } : undefined}
                onTouchStart={zoomed ? (e) => {
                  if (e.target.closest('[data-testid="toolbar-group"]')) return;
                  const touch = e.touches[0]; const rect = e.currentTarget.getBoundingClientRect();
                  const startX = touch.clientX; const startY = touch.clientY;
                  const origin = panOrigin || { x: currentZone?.x + currentZone?.width / 2 || 50, y: currentZone?.y + currentZone?.height / 2 || 50 };
                  setPanning(true);
                  const move = (ev) => { ev.preventDefault(); const t = ev.touches[0]; const dx = (t.clientX - startX) / rect.width * -40; const dy = (t.clientY - startY) / rect.height * -40; setPanOrigin({ x: Math.max(0, Math.min(100, origin.x + dx)), y: Math.max(0, Math.min(100, origin.y + dy)) }); };
                  const up = () => { setPanning(false); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up); };
                  window.addEventListener('touchmove', move, { passive: false }); window.addEventListener('touchend', up);
                } : undefined}
                style={zoomed ? { cursor: panning ? 'grabbing' : 'grab', touchAction: 'none' } : undefined}>
                <div data-testid="zoom-inner" className="w-full h-full"
                  style={zoomed && currentZone ? {
                    transform: `scale(${zoomScale})`,
                    transformOrigin: `${(panOrigin || { x: currentZone.x + currentZone.width / 2, y: currentZone.y + currentZone.height / 2 }).x}% ${(panOrigin || { x: currentZone.x + currentZone.width / 2, y: currentZone.y + currentZone.height / 2 }).y}%`,
                    transition: panning ? 'none' : 'transform 0.5s ease-out, transform-origin 0.3s ease-out',
                  } : { transition: 'transform 0.5s ease-out' }}>
                  {currentImage ? (
                    <img src={`${API}/api/files/${currentImage}`} alt={product.title} className="w-full h-full object-contain p-[8%] transition-opacity duration-300" data-testid="product-image" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                      <Package className="w-16 h-16 text-slate-200 dark:text-slate-700" />
                      {activeView && <span className="text-xs text-slate-400">{activeView.name} preview coming soon</span>}
                    </div>
                  )}
                  {/* Print zone outline */}
                  {currentZone && (
                    <div className={`absolute border-2 border-dashed rounded-sm pointer-events-none transition-all duration-300 ${hasAnyImageOnView ? 'border-[#00E5FF]/20' : 'border-[#00E5FF]/50'}`}
                      style={{ left: `${currentZone.x}%`, top: `${currentZone.y}%`, width: `${currentZone.width}%`, height: `${currentZone.height}%` }}
                      data-testid="product-print-zone">
                      {!hasAnyImageOnView && (
                        <span className="absolute -top-5 left-0 text-[8px] leading-none whitespace-nowrap bg-[#00E5FF]/90 text-[#0A0A0A] font-bold px-1.5 py-0.5 rounded-sm">
                          {activeViewFinishBlocked ? getViewFallbackFinish(activeView) : (selectedFinish?.label || 'Print')} Zone
                        </span>
                      )}
                      {showGrid && (
                        <div className="absolute inset-0 pointer-events-none" data-testid="grid-overlay">
                          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-[#00E5FF]/30" />
                          <div className="absolute left-2/3 top-0 bottom-0 w-px bg-[#00E5FF]/30" />
                          <div className="absolute top-1/3 left-0 right-0 h-px bg-[#00E5FF]/30" />
                          <div className="absolute top-2/3 left-0 right-0 h-px bg-[#00E5FF]/30" />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Image layers — each independently draggable */}
                  {currentZone && currentImages.map((imgItem, idx) => (
                    <div key={imgItem.id}
                      className="absolute"
                      style={{
                        left: `${currentZone.x}%`, top: `${currentZone.y}%`,
                        width: `${currentZone.width}%`, height: `${currentZone.height}%`,
                        zIndex: selectedImageId === imgItem.id ? 20 : 10 + idx,
                      }}
                      onClick={() => { setSelectedImageId(imgItem.id); setSelectedElement(`img-${imgItem.id}`); }}
                      data-testid={`logo-overlay-${imgItem.id}`}>
                      <DraggableOverlay
                        logoSrc={imgItem.userLogo}
                        placement={imgItem.logoPlacement}
                        onPlacementChange={(p) => handlePlacementChange(imgItem.id, p)}
                      />
                    </div>
                  ))}
                  {/* Text layers — pointer-events only on the text, not the full zone container */}
                  {currentZone && currentTexts.map((textItem, idx) => (
                    <div key={textItem.id}
                      className="absolute pointer-events-none"
                      style={{
                        left: `${currentZone.x}%`, top: `${currentZone.y}%`,
                        width: `${currentZone.width}%`, height: `${currentZone.height}%`,
                        zIndex: selectedElement === `text-${textItem.id}` ? 25 : 15 + idx,
                      }}>
                      <div className="pointer-events-auto" onClick={() => { setActiveTextId(textItem.id); setSelectedElement(`text-${textItem.id}`); }}>
                        <TextLayer
                          textItem={textItem}
                          onUpdate={handleUpdateText}
                          onDelete={handleDeleteText}
                          containerRef={textZoneRef}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Toolbar — bottom of canvas */}
                <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center justify-between" data-testid="toolbar-group">
                  {/* Left side — element tools (only when something selected) */}
                  <div className="flex items-center gap-1">
                    {hasSelection && (
                      <>
                        <button onClick={handleRotate} data-testid="toolbar-rotate" title="Rotate 90°"
                          className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] transition-all">
                          <RotateCw className="w-3.5 h-3.5" />
                        </button>
                        {isImageSelected && (
                          <button onClick={handleFlip} data-testid="toolbar-flip" title="Flip Horizontal"
                            className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] transition-all">
                            <FlipHorizontal2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={handleAlignH} data-testid="toolbar-align-h" title="Centre Horizontal"
                          className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] transition-all">
                          <AlignCenterHorizontal className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={handleAlignV} data-testid="toolbar-align-v" title="Centre Vertical"
                          className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] transition-all">
                          <AlignCenterVertical className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  {/* Right side — view tools */}
                  <div className="flex items-center gap-1.5">
                    {hasAnyImageOnView && (
                      <button onClick={() => setShowPreview(true)} data-testid="preview-btn" title="Preview"
                        className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#0A0A0A]/80 dark:hover:bg-violet-400/20 transition-all">
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {currentZone && (
                      <>
                        <button onClick={() => setShowGrid(g => !g)} data-testid="grid-toggle" title="Show Grid"
                          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${showGrid ? 'bg-[#00E5FF] text-[#0A0A0A] shadow-lg shadow-cyan-500/20' : 'bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#0A0A0A]/80 dark:hover:bg-violet-400/20'}`}>
                          <Grid3x3 className="w-4 h-4" />
                        </button>
                        <button onClick={cycleZoom} data-testid="zoom-toggle"
                          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${zoomed ? 'bg-[#00E5FF] text-[#0A0A0A] shadow-lg shadow-cyan-500/20' : 'bg-[#0A0A0A]/60 dark:bg-white/10 text-white backdrop-blur-sm hover:bg-[#0A0A0A]/80 dark:hover:bg-violet-400/20'}`}>
                          {zoomed ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
                          {zoomed && <span className="absolute -top-1.5 -right-1.5 bg-[#0A0A0A] text-[#00E5FF] text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{zoomLevel === 1 ? '1' : '2'}</span>}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {views.length > 1 && (
                <div className="grid grid-cols-2 sm:flex gap-2 mt-4" data-testid="view-tabs">
                  {views.map((v, i) => {
                    const hasLogo = viewLogos[v.id]?.length > 0;
                    const extraCost = v.print_price || 0;
                    const finishBlocked = selectedFinish && !viewAllowsFinish(v, selectedFinish);
                    return (
                      <button key={v.id} onClick={() => setActiveViewIdx(i)} data-testid={`view-tab-${v.id}`}
                        className={`relative flex-1 min-w-0 py-2.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap px-3 ${activeViewIdx === i ? 'bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-violet-400/10'}`}>
                        <span className="flex items-center justify-center gap-1.5">
                          {v.name}
                          {finishBlocked && <span className="text-[8px] text-amber-400 font-normal">{getViewFallbackFinish(v)}</span>}
                          {hasLogo && <CheckCircle2 className="w-3 h-3 text-green-400" />}
                          {extraCost > 0 && !hasLogo && <span className={`text-[9px] font-medium ${activeViewIdx === i ? 'opacity-70' : 'text-[#00E5FF]'}`}>+£{extraCost.toFixed(2)}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Product Details & Customiser */}
            <div>
              <div className="mb-5">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">{product.category}</span>
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white tracking-tight mt-2">{product.title}</h1>
                {product.description && (
                  <div className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: product.description }} />
                )}
              </div>

              {/* Finish Selector */}
              {hasFinishes && (
                <div className="mb-5">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-2">Finish</label>
                  <div className="flex flex-wrap gap-2" data-testid="finish-selector">
                    {productFinishes.map((f) => {
                      const isSelected = selectedFinish?.label === f.label;
                      return (
                        <button key={f.label} onClick={() => setSelectedFinish(f)}
                          data-testid={`finish-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all border ${isSelected ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF]/30'}`}>
                          {isSelected && <Check className="w-3 h-3" />}
                          {f.label}
                          {f.price_modifier > 0 && <span className="text-[10px] text-slate-400 dark:text-slate-500">+£{f.price_modifier}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Finish restriction notice */}
              {activeViewFinishBlocked && (
                <div className="mb-4 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2" data-testid="finish-blocked-notice">
                  <span className="text-amber-400 text-sm mt-0.5">&#9432;</span>
                  <p className="text-[11px] text-amber-300/90 leading-relaxed">
                    <strong>{activeView.name}</strong> will be <strong>{getViewFallbackFinish(activeView)}</strong> — <s>{selectedFinish.label.toLowerCase()}</s> isn't available for this side. You can still upload your design as normal.
                  </p>
                </div>
              )}

              {/* Image Upload — per view (supports multiple) */}
              <div className="mb-6 p-4 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-3">
                  {activeView?.name || 'View'} — Images / Designs
                  {(activeView?.print_price || 0) > 0 && !hasAnyImageOnView && (
                    <span className="ml-2 text-[#00E5FF] normal-case font-medium">(+£{activeView.print_price.toFixed(2)} for {activeView.name} print)</span>
                  )}
                </label>
                {hasAnyImageOnView ? (
                  <div className="space-y-2">
                    {/* Image thumbnails */}
                    <div className="flex flex-wrap gap-2">
                      {currentImages.map((img) => (
                        <div key={img.id}
                          onClick={() => { setSelectedImageId(img.id); setSelectedElement(`img-${img.id}`); }}
                          className={`relative w-14 h-14 rounded-lg border overflow-hidden bg-white dark:bg-[#111111] cursor-pointer transition-all group ${selectedImageId === img.id ? 'border-[#00E5FF] ring-1 ring-[#00E5FF]/30' : 'border-slate-200 dark:border-white/10 hover:border-[#00E5FF]/40'}`}
                          data-testid={`image-thumb-${img.id}`}>
                          <img src={img.userLogo} alt="Design" className="w-full h-full object-contain" />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveImage(img.id); }}
                            className="absolute top-0 right-0 w-4 h-4 bg-rose-500 rounded-bl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-image-${img.id}`}>
                            <Trash2 className="w-2 h-2 text-white" />
                          </button>
                        </div>
                      ))}
                      {/* Add another image */}
                      <button onClick={() => fileInputRef.current?.click()} data-testid="add-another-image-btn"
                        className="w-14 h-14 rounded-lg border border-dashed border-slate-300 dark:border-white/10 flex items-center justify-center hover:border-[#00E5FF]/40 transition-colors cursor-pointer">
                        <Plus className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                    {/* Resolution for selected image */}
                    {selectedImg && (
                      <div className="flex items-center gap-2">
                        {resolution && (
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${resolution.bg}`} data-testid="resolution-indicator">
                            <ResIcon className="w-3.5 h-3.5" style={{ color: resolution.color }} />
                            <span style={{ color: resolution.color }}>{resolution.label}</span>
                            <span className="text-slate-500 ml-1">({selectedImg.originalWidth}px)</span>
                          </div>
                        )}
                      </div>
                    )}
                    <button onClick={() => setShowQRTool(true)} data-testid="qr-tool-btn"
                      className="w-full flex items-center justify-center gap-1.5 py-2 border border-slate-200 dark:border-white/10 rounded-xl hover:border-[#00E5FF]/30 transition-colors text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-[#00E5FF]">
                      <QrCode className="w-3.5 h-3.5" /> Add QR Code
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button onClick={() => fileInputRef.current?.click()} data-testid="upload-logo-btn"
                      className="w-full flex flex-col items-center justify-center py-6 border border-dashed border-slate-300 dark:border-white/10 rounded-xl hover:border-[#00E5FF]/40 dark:hover:border-[#00E5FF]/40 transition-colors cursor-pointer">
                      <Upload className="w-5 h-5 text-slate-400 dark:text-slate-500 mb-2" />
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Upload {activeView?.name} logo or design</span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-600 mt-1">PNG, JPG — best at 2000px+ width</span>
                    </button>
                    <button onClick={() => setShowQRTool(true)} data-testid="qr-tool-btn"
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-slate-200 dark:border-white/10 rounded-xl hover:border-[#00E5FF]/30 transition-colors text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-[#00E5FF]">
                      <QrCode className="w-3.5 h-3.5" /> Generate QR Code
                    </button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="logo-file-input" />
              </div>

              {/* Text Layer Controls */}
              {currentZone && (
                <div className="mb-6 p-4 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl" ref={textZoneRef}>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-3">
                    <Type className="w-3 h-3 inline mr-1" /> Text Layers
                  </label>
                  <TextControlPanel
                    textItems={currentTexts}
                    activeTextId={activeTextId}
                    onAdd={handleAddText}
                    onUpdate={handleUpdateText}
                    onDelete={handleDeleteText}
                    onSelect={(id) => { setActiveTextId(id); setSelectedElement(`text-${id}`); }}
                  />
                </div>
              )}

              {/* Per-view summary */}
              {views.length > 1 && viewsWithLogos > 0 && (
                <div className="mb-5 p-3 bg-[#00E5FF]/5 border border-[#00E5FF]/10 rounded-xl">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#00E5FF]/70 mb-2">Print Sides</p>
                  <div className="flex flex-wrap gap-2">
                    {views.map(v => {
                      const has = viewLogos[v.id]?.length > 0;
                      return (
                        <span key={v.id} className={`text-xs px-2.5 py-1 rounded-lg font-medium ${has ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20' : 'bg-slate-100 dark:bg-white/5 text-slate-400 border border-slate-200 dark:border-white/10'}`}>
                          {v.name} {has && <CheckCircle2 className="w-3 h-3 inline ml-0.5" />}
                          {has && (v.print_price || 0) > 0 && <span className="ml-1 text-[10px]">+£{v.print_price.toFixed(2)}</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Colour Variants */}
              {colors.length > 0 && (
                <div className="mb-5">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2 flex items-center gap-1.5">
                    <Palette className="w-3 h-3" /> Colour {selectedColor && <span className="normal-case font-medium text-slate-300">— {selectedColor.name}</span>}
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="color-swatches">
                    {colors.map((color, i) => (
                      <button key={i} onClick={() => setSelectedColorIdx(i)} title={color.name}
                        data-testid={`color-swatch-${color.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`w-9 h-9 rounded-xl border-2 transition-all flex items-center justify-center ${selectedColorIdx === i ? 'border-[#00E5FF] scale-110 shadow-lg shadow-cyan-500/20' : 'border-slate-300 dark:border-white/20 hover:border-[#00E5FF]/50 hover:scale-105'}`}
                        style={{ backgroundColor: color.hex_code }}>
                        {selectedColorIdx === i && <Check className={`w-4 h-4 ${isLightColor(color.hex_code) ? 'text-slate-800' : 'text-white'}`} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sub-Options */}
              {product.sub_options?.length > 0 && (
                <div className="space-y-4 mb-6">
                  {product.sub_options.map((group) => (
                    <div key={group.name}>
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-2">{group.name}</label>
                      <div className="flex flex-wrap gap-2">
                        {group.options?.map((option) => {
                          const selected = selectedOptions[group.name]?.label === option.label;
                          return (
                            <button key={option.label} onClick={() => selectOption(group.name, option)} data-testid={`option-${group.name}-${option.label}`}
                              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all border ${selected ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF]/30'}`}>
                              {selected && <Check className="w-3 h-3" />}
                              {option.label}
                              {option.price_modifier > 0 && <span className="text-[10px] text-slate-400 dark:text-slate-500">+£{option.price_modifier}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Price + Buttons */}
              <div className="border-t border-slate-200 dark:border-white/10 pt-6">
                <div className="flex items-end gap-2 mb-2">
                  <span className="font-['Outfit'] text-4xl font-black text-[#0A0A0A] dark:text-white" data-testid="product-total-price">£{totalPrice.toFixed(2)}</span>
                  <span className="text-sm text-slate-500 mb-1">per unit</span>
                </div>
                {(viewPrintTotal > 0 || finishCost > 0) && (
                  <p className="text-xs text-slate-400 mb-4">
                    {finishCost > 0 && `${selectedFinish.label} +£${finishCost.toFixed(2)}`}
                    {finishCost > 0 && viewPrintTotal > 0 && ' · '}
                    {viewPrintTotal > 0 && `Extra sides +£${viewPrintTotal.toFixed(2)}`}
                  </p>
                )}
                <div className="flex items-center gap-3 mb-5" data-testid="qty-selector">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500">Qty</label>
                  <div className="inline-flex items-center border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
                    <button onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="qty-minus" className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-violet-400/5 transition-colors text-lg font-medium">-</button>
                    <span className="w-10 h-10 flex items-center justify-center text-sm font-semibold text-[#0A0A0A] dark:text-white border-x border-slate-200 dark:border-white/10" data-testid="qty-value">{qty}</span>
                    <button onClick={() => setQty(q => q + 1)} data-testid="qty-plus" className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-violet-400/5 transition-colors text-lg font-medium">+</button>
                  </div>
                  {qty > 1 && <span className="text-xs text-slate-400">£{(totalPrice * qty).toFixed(2)} total</span>}
                </div>
                {isSoldOut ? (
                  <div className="w-full bg-rose-500/10 border border-rose-500/20 text-rose-400 py-4 rounded-xl font-semibold text-sm text-center" data-testid="sold-out-banner">
                    Sold Out
                  </div>
                ) : hasAnyLogo ? (
                  <div className="flex gap-3">
                    <button onClick={handleCheckoutNow} data-testid="checkout-now-btn"
                      className="flex-1 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white py-4 rounded-xl font-semibold text-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all flex items-center justify-center gap-2">
                      Checkout Now
                    </button>
                    <button onClick={handleAddToCart} data-testid="add-to-basket-btn"
                      className={`flex-1 py-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 border ${addedToCart ? 'bg-green-500 border-green-500 text-white' : 'border-slate-200 dark:border-white/10 text-[#0A0A0A] dark:text-white hover:border-[#00E5FF]/50'}`}>
                      {addedToCart ? <><CheckCircle2 className="w-4 h-4" /> Added</> : <><ShoppingBag className="w-4 h-4" /> Add to Basket</>}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()} data-testid="upload-logo-btn-main"
                    className="w-full bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white py-4 rounded-xl font-semibold text-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all">
                    Upload Logo to Continue
                  </button>
                )}
                {!hasAnyLogo && <p className="text-[10px] text-slate-400 dark:text-slate-600 text-center mt-3">Upload your logo above — it stays when you switch colours and views</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showCropModal && rawImageSrc && (
        <CropModal imageSrc={rawImageSrc} onConfirm={handleCropConfirm} onCancel={() => { setShowCropModal(false); setRawImageSrc(null); }} />
      )}

      {showPreview && (
        <PreviewModal
          product={product}
          activeView={activeView}
          currentImage={currentImage}
          currentZone={currentZone}
          userLogo={firstImage?.userLogo}
          logoPlacement={firstImage?.logoPlacement || { x: 0, y: 0, width: 100, height: 100 }}
          allImages={currentImages}
          selectedFinish={selectedFinish}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showQRTool && (
        <QRCodeTool onGenerate={handleQRGenerate} onClose={() => setShowQRTool(false)} />
      )}

      <Footer />
    </div>
  );
}
